<template>
  <div class="detail-description text-font-1">
    <Resource
      :src="bgUrl"
      class="absolute left-0 top-0 w-full"
      fetchPriority="hight"
      :lazy="false"
    />
    <div class="w-[1200px] ms:w-full mx-auto flex relative">
      <div class="py-[80px] w-[64%] min-h-[800px]">
        <h1 class="text-[48px] font-bold m-0">
          {{ character?.name }}
        </h1>
        <div class="mt-[16px] flex">
          <template v-if="character?.config?.age">
            <div>
              {{ $t('nian-ling') }}:
              {{ character?.config?.age || 'xx' }}
            </div>
            <DividerVertical />
          </template>
          <template v-if="character?.config?.gender">
            <div>
              {{ $t('xing-bie') }}:
              {{ character?.config?.gender === 'Male' ? $t('nan') : $t('nv') }}
            </div>
            <DividerVertical />
          </template>
          <template v-if="character?.online_time">
            <div>
              {{ $t('dan-sheng-ri-qi') }}:
              {{ timeFormat(character?.online_time, 'YYYY-MM-DD') }}
            </div>
            <DividerVertical />
          </template>
          <div v-if="character?.homepage_org_id" class="flex items-center">
            <span>{{ $t('chuang-jian-zhe') }}: </span>
            <NuxtLink
              :to="{
                name: PageKey.PERSONAL_PAGE,
                params: { key: character?.homepage_org_id },
              }"
              class="flex items-center ml-1"
            >
              <Resource
                class="h-[18px] w-[18px]"
                :src="character?.avatar_url"
                :transform-resource="transformImage"
              />
              <span class="ml-[4px]">{{ character?.homepage_org_name }}</span>
            </NuxtLink>
          </div>
        </div>
        <div class="detail-info">
          <div v-if="character?.description" class="p-[20px]">
            <div class="text-[15px] font-semibold">
              {{ $t('ren-wu-jie-shao') }}:
            </div>
            <p
              class="text-12 leading-[24px] mt-[10px] font-medium text-font-70"
            >
              <template v-if="character?.description">
                {{ character?.description }}
              </template>
            </p>
          </div>
          <div
            v-if="character?.description && tags.length"
            class="bg-back-3 h-[1px]"
          ></div>
          <div class="tags">
            <TagCom v-for="tag in tags" :key="tag.tag_id" :tag="tag" />
          </div>
        </div>
        <div class="mt-[40px] flex">
          <FlatBtn type="gradient" @click="createWork">
            {{ $t('yong-ta-chuang-zuo') }}
          </FlatBtn>
          <DividerVertical bg-color="var(--back-5)" height="80%" width="60px" />
          <Collect
            v-if="character"
            class="big-collect"
            :model-value="character?.is_star_personal"
            :params="{
              model_type: COLLECTION_MAP.SOURCE,
              model_id: character.id,
              resource_type: RESOURCE_TYPE.PERSON,
              source: COLLECTION_SOURCE.YOUYAN,
            }"
            @update:model-value="(v) => (character!.is_star_personal = v)"
          >
            <template #default="slotProps">
              <iconpark-icon
                :name="slotProps.like ? 'like-outlined' : 'unlike-outlined'"
                class="text-[24px]"
                :class="slotProps.like ? 'text-primary' : 'text-font-1'"
              ></iconpark-icon>
            </template>
          </Collect>
          <TemplateShare
            class="!h-[48px] !w-[48px] ml-[16px] bg-back-bg"
            :isCard="true"
            iconClass=""
            :title="$t('fen-xiang-3d-shu-zi-ren')"
            :params="{
              share_ids: [character?.id],
              model_type: COLLECTION_MAP.SOURCE,
            }"
            :trackParams="{
              source: resourceLocation?.source,
              id: character?.id,
              tags: character?.tags,
              look_name: character?.look_name,
              name: character?.name,
            }"
            :eventName="TRACK_EVENT.YOUYAN_PLATFORM_SHARE_CHARACTER"
            :shareToInnerUser="false"
            :linkUrl="`${location?.origin}/3d-avatar/${character?.identity}.html`"
          >
            <iconpark-icon name="share-v2" class="text-[24px] text-font-1" />
          </TemplateShare>
        </div>
      </div>
      <Resource
        class="flex-1 w-[600px] absolute bottom-[40px] right-[-150px] pointer-events-none"
        :src="character?.cover_url"
        :resource-option="{
          format: 'webp',
          resize: {
            w: 1000,
            m: 'lfit',
          },
        }"
      />
    </div>
    <Resource
      :transform-resource="transformImage"
      class="absolute bottom-0 w-full pointer-events-none"
      :src="
        getFeUrl(
          getEnv('area') === 'CN'
            ? '/youyan-platform/png/digital-human-mask2.png'
            : '/youyan-platform/png/digital-human-rendora-mask4.png'
        )
      "
    >
    </Resource>
  </div>
</template>
<script lang="ts" setup>
import TagCom from '../tag.vue'
import DividerVertical from '../divider-vertical.vue'
import FlatBtn from '../flat-btn.vue'
import type { DetailTag } from './interface'
import type { Character } from '~/api/interface/character'
import Collect from '@/features/collect/index.vue'
import { TemplateShare } from '@/features/template/components/templateShare'
import { PageKey } from '~/constants/page'

import { useTime } from '~/hooks/useTime'

interface DetailDescriptionProps {
  character?: Character | null
  tags: DetailTag[]
}

defineProps<DetailDescriptionProps>()

const _createWork = inject<(source?: SOURCE) => void>('createWork')
const _changeImage = inject<(source?: SOURCE) => void>('changeImage')

const resourceLocation = injectResourceLocation()

function createWork() {
  _createWork?.(resourceLocation?.source)
}

function changeImage() {
  _changeImage?.(resourceLocation?.source)
}

const { timeFormat } = useTime()

const location = computed(() => {
  if (import.meta.client) {
    return window.location
  }

  return null
})

const bgUrl = transformImage(
  getFeUrl(
    getEnv('area') === 'CN'
      ? '/youyan-platform/png/digital-human-detail-bg2.png'
      : '/youyan-platform/png/digital-human-detail-rendora-bg2.png'
  )
)
</script>
<style lang="scss" scoped>
.detail-description {
  // background-image: v-bind(bgUrl);
  // background-size: 100% 100%;
  // background-repeat: no-repeat;
  // height: 47.2vw;
  position: relative;
  overflow: hidden;

  .collect-like {
    path {
      fill: var(--text-1);
    }
  }

  .big-collect {
    height: 48px !important;
    width: 48px !important;
    box-shadow: 0px 2px 6px 0px rgba(86, 80, 220, 0.1);
    background-color: var(--back-bg);
  }

  .detail-info {
    @apply rounded-[12px] mt-[36px] relative z-[1];
    background: var(--character-detail-bg);
    border: 1px solid var(--back-3);
  }

  .tags {
    @apply p-[20px];

    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-flow: dense;
    column-gap: 16px;
    row-gap: 20px;
    // column-count: 2;
    // column-gap: 10px;
  }
}
</style>
